import { useToast } from 'hooks'
import { useCallback, useState } from 'react'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'
import { claimReferralReward, type ClaimableReward } from './referrals.service'
import { useReferralProgram } from './use-referral-program'

/**
 * Shape returned to the caller after a claim. `granted` is authoritative — the
 * server treats already-claimed rows as no-ops, so `granted` may be zero even
 * when the request succeeded (someone else's tab already claimed them).
 */
export interface ClaimResult {
  granted: number
  businessName: string
}

interface UseClaimReferralRewardResult {
  /**
   * True while a claim POST is in flight. Callers disable Claim buttons on this
   * so a double-click / rapid re-open doesn't send two identical requests.
   */
  claiming: boolean
  /**
   * Fire the claim against `businessId` for the given `rewards`, show a
   * celebration toast on success, refetch the referral info so `claimable`
   * empties immediately, and resolve with `{ granted, businessName }` (or null
   * on failure). Never throws — errors are reported via toast + error tracking.
   *
   * Pass `{ silent: true }` to suppress the SUCCESS toast — used by the
   * single-business celebration modal, which is itself the celebration, so a
   * toast on top would double up. Error toasts still fire.
   */
  claim: (
    rewards: ClaimableReward[],
    businessId: string,
    businessName: string,
    options?: { silent?: boolean }
  ) => Promise<ClaimResult | null>
}

/**
 * The single client entry point for consuming approved referral rewards. Used
 * by both the celebration auto-mount (in `AuthAppLayout`) and the "credits
 * waiting to be claimed" card on `ReferralPage`, so the toast, the refetch,
 * the error handling, and the analytics/logging stay identical between
 * surfaces without either surface knowing about the other.
 */
export function useClaimReferralReward(): UseClaimReferralRewardResult {
  const { refetch } = useReferralProgram()
  const { showSuccess, showError } = useToast()
  const [claiming, setClaiming] = useState(false)

  const claim = useCallback(
    async (
      rewards: ClaimableReward[],
      businessId: string,
      businessName: string,
      options?: { silent?: boolean }
    ): Promise<ClaimResult | null> => {
      if (rewards.length === 0 || !businessId) return null
      setClaiming(true)
      const ids = rewards.map(r => r.referralId)
      const totalCredits = rewards.reduce((sum, r) => sum + r.credits, 0)
      try {
        const { granted } = await claimReferralReward(ids, businessId)
        // Refresh /me so `claimable` empties and the sidebar dot / waiting
        // card / celebration all disappear on the same tick. Await so the
        // caller can rely on state being consistent when the toast fires.
        await refetch()
        if (!options?.silent) {
          // Prefer the server's `granted` in the toast when it differs from the
          // requested total — matches what the user's balance actually moved by.
          const displayCredits = granted > 0 ? granted : totalCredits
          showSuccess(
            `+${displayCredits} credits. A friend you invited subscribed. Added to ${businessName}.`
          )
        }
        return { granted, businessName }
      } catch (error) {
        console.error('Failed to claim referral reward:', error)
        errorReportingService.captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            businessId,
            referralIds: ids,
          }
        )
        showError('Could not claim your referral reward. Please try again.')
        return null
      } finally {
        setClaiming(false)
      }
    },
    [refetch, showSuccess, showError]
  )

  return { claim, claiming }
}

/**
 * Stable stringified key for a set of claimable IDs. Used to persist the
 * "dismissed once" flag in localStorage — a new approval brings a new ID, so
 * the derived key differs and the dialog re-opens. Sorted so key equality is
 * order-independent.
 */
export const claimableDismissKey = (rewards: ClaimableReward[]): string =>
  rewards
    .map(r => r.referralId)
    .slice()
    .sort()
    .join(',')
